/**
 * First-message gating from non-contacts ("message requests").
 *
 * Mirrors Signal's behaviour: a message from someone outside the local
 * contact roster is *decrypted* (we still need to advance the ratchet) but
 * is NOT rendered in the main chat list. It surfaces in a separate "Message
 * requests" inbox where the user picks Accept / Block / Delete.
 *
 * Until the user decides:
 *   - sends from us back to them are unaffected (the user can choose to
 *     reply directly from the request banner, which implicitly accepts)
 *   - we suppress every side-channel signal that would tell the sender
 *     "your target is online and reading you": delivery receipts, read
 *     receipts, typing indicators
 *   - if the same sender sends more before we decide, the request entry
 *     bumps a counter; we don't create a new request per message
 *
 * This file: pure value types and the state-machine alphabet. The store
 * (MessageRequestStore.ts) drives the transitions; the policy
 * (MessageRequestPolicy.ts) maps state → outbound-suppression decisions
 * without touching disk.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/MessageRequest.swift` exactly.
 */

/**
 * First-message-gating state.
 *
 * requested — First-message-gated. Decrypted but quarantined from the main
 *             chat list.
 * accepted  — User explicitly OK'd the conversation (or we OK'd them
 *             implicitly by having them in the contact roster). Renders
 *             normally.
 * blocked   — User said "stop." Subsequent messages from them are still
 *             decrypted (ratchet state must advance, otherwise we'd corrupt
 *             the session if the user later unblocks) but never surface in
 *             any UI.
 */
export type MessageRequestState = "requested" | "accepted" | "blocked";

/**
 * Persisted record for a single peer's message-request entry.
 */
export interface MessageRequestRecord {
  readonly userId: string;
  readonly state: MessageRequestState;

  /**
   * First time we observed an incoming message from this user that triggered
   * this record (i.e. the moment they first entered our world without being a
   * contact). Stable across all subsequent transitions.
   */
  readonly firstSeenAt: number; // Unix epoch ms

  /**
   * Time of the most recent incoming message we observed from them, regardless
   * of state. Used by the requests-inbox UI to sort.
   */
  readonly lastMessageAt: number; // Unix epoch ms

  /**
   * Number of incoming messages observed since this record was created.
   * Increments on every observe() call, including when the record is
   * 'blocked' (so we have a spam metric for a future "report" flow).
   */
  readonly messageCount: number;

  /**
   * Snippet of the most recent message's plaintext, capped to
   * MESSAGE_REQUEST_PREVIEW_MAX_LENGTH chars. Set only while 'requested' so
   * the requests-inbox can preview without asking the renderer. Cleared on
   * transition to 'accepted' (preview becomes the live conversation) or
   * 'blocked' (we don't render at all).
   */
  readonly previewSnippet: string | null;

  /**
   * Number of incoming messages received *while* in 'blocked'. Resets to 0
   * on any non-blocked transition. Useful for spam-reporting volume.
   */
  readonly blockedAttemptCount: number;
}

/**
 * What the chat-list/renderer should do with a freshly-decrypted message
 * after MessageRequestStore.observe() returns. Mirrors iOS
 * `MessageRequestDisposition`.
 */
export type MessageRequestDisposition =
  | { kind: "render" }
  | { kind: "requestBanner"; record: MessageRequestRecord }
  | { kind: "suppress" };

/** Convenience singletons for the no-payload variants. */
export const Render: MessageRequestDisposition = { kind: "render" };
export const Suppress: MessageRequestDisposition = { kind: "suppress" };

/** Maximum length of the preview snippet stored on a 'requested' record. */
export const MESSAGE_REQUEST_PREVIEW_MAX_LENGTH = 80;

/**
 * Truncate a plaintext to a preview-safe size. Newlines are flattened to
 * spaces so the inbox cell doesn't wrap unpredictably.
 *
 * Mirrors `messageRequestPreview(from:)` in MessageRequest.swift.
 */
export function messageRequestPreview(plaintext: string): string {
  const flattened = plaintext.replace(/\n/g, " ").trim();
  if (flattened.length <= MESSAGE_REQUEST_PREVIEW_MAX_LENGTH) {
    return flattened;
  }
  return flattened.slice(0, MESSAGE_REQUEST_PREVIEW_MAX_LENGTH) + "…";
}

/**
 * Errors thrown by MessageRequestStore manual transitions.
 */
export type MessageRequestError =
  | { kind: "notFound" }
  | { kind: "invalidTransition"; from: MessageRequestState; action: string };

export class MessageRequestNotFoundError extends Error {
  readonly kind = "notFound" as const;
  constructor() {
    super("MessageRequestStore: record not found");
    this.name = "MessageRequestNotFoundError";
  }
}

export class MessageRequestInvalidTransitionError extends Error {
  readonly kind = "invalidTransition" as const;
  readonly from: MessageRequestState;
  readonly action: string;
  constructor(from: MessageRequestState, action: string) {
    super(
      `MessageRequestStore: invalid transition from '${from}' via '${action}'`,
    );
    this.name = "MessageRequestInvalidTransitionError";
    this.from = from;
    this.action = action;
  }
}
